import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, DotsThreeVertical } from "@phosphor-icons/react";
import { toast } from "sonner";

import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import { api } from "@/lib/api";
import { Field, Modal } from "@/pages/Merchants";

const FEE_TYPES = ["card_issuance", "card_load", "card_spend", "monthly"];

const emptyForm = () => ({
  merchant_id: "",
  card_type_id: "",
  fee_type: "card_issuance",
  amount: 0,
  percentage: 0,
});

export default function Fees() {
  const [items, setItems] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => api.get("/admin/fees").then((r) => setItems(r.data.items)), []);
  useEffect(() => {
    load();
    api.get("/admin/merchants").then((r) => setMerchants(r.data.items));
    api.get("/admin/card-types").then((r) => setCardTypes(r.data.items));
  }, [load]);

  const openCreate = () => {
    setForm(emptyForm());
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (f) => {
    setEditing(f);
    setForm({
      merchant_id: f.merchant_id || "",
      card_type_id: f.card_type_id || "",
      fee_type: f.fee_type,
      amount: f.amount,
      percentage: f.percentage,
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/admin/fees/${editing.id}`, {
          amount: Number(form.amount),
          percentage: Number(form.percentage),
        });
        toast.success("Fee updated");
      } else {
        await api.post("/admin/fees", {
          merchant_id: form.merchant_id || null,
          card_type_id: form.card_type_id || null,
          fee_type: form.fee_type,
          amount: Number(form.amount),
          percentage: Number(form.percentage),
        });
        toast.success("Fee created");
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const toggle = async (f) => {
    try {
      await api.patch(`/admin/fees/${f.id}`, { active: !f.active });
      toast.success(f.active ? "Fee disabled" : "Fee enabled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const merchantsById = Object.fromEntries(merchants.map((m) => [m.id, m.company_name]));
  const ctById = Object.fromEntries(cardTypes.map((c) => [c.id, c.name]));

  const scopeLabel = (f) => (
    <>
      {f.merchant_id ? merchantsById[f.merchant_id] || "merchant" : <span className="text-zinc-400">all merchants</span>}
      {" · "}
      {f.card_type_id ? ctById[f.card_type_id] || "card type" : <span className="text-zinc-400">any card</span>}
    </>
  );

  return (
    <Layout>
      <div className="p-6 lg:p-10" data-testid="fees-page">
        <PageHeader
          title="Fees"
          subtitle="Configure issuance, load, spend and monthly fees per merchant + card type."
          actions={
            <button onClick={openCreate} data-testid="create-fee-btn" className="h-9 px-4 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150">
              <Plus size={14} weight="bold" /> New fee rule
            </button>
          }
        />

        <Surface className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Scope</th>
                <th className="px-5 py-3 text-right">Fixed</th>
                <th className="px-5 py-3 text-right">%</th>
                <th className="px-5 py-3">Active</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 font-mono text-xs">{f.fee_type}</td>
                  <td className="px-5 py-3 text-xs">{scopeLabel(f)}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">${Number(f.amount).toFixed(2)}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{Number(f.percentage).toFixed(2)}%</td>
                  <td className="px-5 py-3"><StatusPill value={f.active ? "active" : "suspended"} /></td>
                  <td className="px-5 py-3 text-right">
                    <FeeActionsMenu
                      fee={f}
                      open={openMenuId === f.id}
                      onOpenChange={(next) => setOpenMenuId(next ? f.id : null)}
                      onEdit={() => { setOpenMenuId(null); openEdit(f); }}
                      onToggle={() => { setOpenMenuId(null); toggle(f); }}
                    />
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-xs text-zinc-500">No fee rules.</td></tr>}
            </tbody>
          </table>
        </Surface>

        {open && (
          <Modal title={editing ? "Edit fee rule" : "New fee rule"} onClose={closeModal}>
            <form onSubmit={submit} className="space-y-3">
              {editing ? (
                <>
                  <div className="text-sm text-zinc-600 space-y-1 rounded-sm border border-zinc-100 bg-zinc-50 px-3 py-2">
                    <div><span className="text-zinc-500">Type:</span> <span className="font-mono text-xs">{editing.fee_type}</span></div>
                    <div><span className="text-zinc-500">Scope:</span> {scopeLabel(editing)}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Fixed amount (USD)" testId="fee-amount" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} />
                    <Field label="Percentage" testId="fee-pct" value={form.percentage} onChange={(v) => setForm({ ...form, percentage: v })} />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Fee type</label>
                    <select data-testid="fee-type" value={form.fee_type} onChange={(e) => setForm({ ...form, fee_type: e.target.value })} className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none">
                      {FEE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Merchant (optional)</label>
                    <select data-testid="fee-merchant" value={form.merchant_id} onChange={(e) => setForm({ ...form, merchant_id: e.target.value })} className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none">
                      <option value="">All merchants (default)</option>
                      {merchants.map((m) => <option key={m.id} value={m.id}>{m.company_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Card type (optional)</label>
                    <select data-testid="fee-card-type" value={form.card_type_id} onChange={(e) => setForm({ ...form, card_type_id: e.target.value })} className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none">
                      <option value="">Any card type</option>
                      {cardTypes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Fixed amount (USD)" testId="fee-amount" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} />
                    <Field label="Percentage" testId="fee-pct" value={form.percentage} onChange={(v) => setForm({ ...form, percentage: v })} />
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150">Cancel</button>
                <button data-testid="submit-fee" className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 transition-colors duration-150">
                  {editing ? "Save changes" : "Create"}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </Layout>
  );
}

function FeeActionsMenu({ fee, open, onOpenChange, onEdit, onToggle }) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const placeMenu = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 160;
    const menuHeight = 88;
    const gap = 4;
    let top = rect.bottom + gap;
    let left = rect.right - menuWidth;
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeight - gap);
    }
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    setMenuStyle({ position: "fixed", top, left, width: menuWidth, zIndex: 50 });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    placeMenu();
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [open, placeMenu]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      onOpenChange(false);
    };
    const onKey = (e) => { if (e.key === "Escape") onOpenChange(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="Fee actions"
        aria-expanded={open}
        data-testid={`fee-actions-${fee.id}`}
        onClick={() => onOpenChange(!open)}
        className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 transition-colors duration-150"
      >
        <DotsThreeVertical size={18} weight="bold" />
      </button>
      {open && menuStyle && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={menuStyle}
          className="min-w-[10rem] rounded-sm border border-zinc-200 bg-white py-1 shadow-lg"
          data-testid={`fee-actions-menu-${fee.id}`}
        >
          <FeeMenuItem onClick={onEdit} testId={`edit-fee-${fee.id}`}>Edit</FeeMenuItem>
          <FeeMenuItem
            onClick={onToggle}
            testId={`toggle-fee-${fee.id}`}
            destructive={fee.active}
          >
            {fee.active ? "Disable" : "Enable"}
          </FeeMenuItem>
        </div>,
        document.body,
      )}
    </>
  );
}

function FeeMenuItem({ children, onClick, testId, destructive }) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      className={`block w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 transition-colors duration-150 ${
        destructive ? "text-red-700" : "text-zinc-900"
      }`}
    >
      {children}
    </button>
  );
}
